import React, { useState, useEffect } from 'react';
import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { useTheme } from '../contexts/AppContexts';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import useImageDrop from '../utils/useImageDrop';
import { 
  Lightbulb, 
  ArrowLeft,
  Save,
  Upload,
  Trash2,
  Image as ImageIcon,
  Camera,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { API_BASE_URL } from '../config';

const defaultDotmplficcOptions = [
  { value: 'Doctrine', label: 'Doctrine', color: '#3b82f6' },
  { value: 'Organization', label: 'Organization', color: '#10b981' },
  { value: 'Training', label: 'Training', color: '#f59e0b' },
  { value: 'Maint and Equip', label: 'Maint and Equip', color: '#8b5cf6' },
  { value: 'Personnel', label: 'Personnel', color: '#06b6d4' },
  { value: 'Leadership', label: 'Leadership', color: '#ef4444' },
  { value: 'Facilities', label: 'Facilities', color: '#84cc16' },
  { value: 'Interoperability', label: 'Interoperability', color: '#f97316' },
  { value: 'Communications', label: 'Communications', color: '#ec4899' },
  { value: 'Command and Control', label: 'Command and Control', color: '#6366f1' }
];

const LessonsLearnedForm = ({ exerciseId, editingLesson, onBack, onSave, dotmplficcOptions = defaultDotmplficcOptions }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    // First Container - Lesson Learned
    name: '',
    serial_number: null,
    priority: 'Pri 1',
    references: '',
    date: new Date().toISOString().split('T')[0],
    lesson_images: [],
    
    // Second Container - Observations and Recommended Actions
    observation_status: '',
    dotmplficc: 'Doctrine',
    ics_organization: 'Command Staff',
    issues_observation: '',
    recommendations: '',
    
    // Third Container - Analysis and Actions
    additional_comments: '',
    recommended_actions: '',
    final_authority_remarks: '',
    testing_done_by: '',
    procedures_written_by: '',
    implement_new_ll_by: '',
    
    // Stage Selection
    stage: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Priority options with colors
  const priorityOptions = [
    { value: 'Pri 1', label: 'Pri 1', color: '#ef4444' }, // Red
    { value: 'Pri 2', label: 'Pri 2', color: '#f59e0b' }, // Orange  
    { value: 'Pri 3', label: 'Pri 3', color: '#22c55e' }, // Green
    { value: 'Pri 4', label: 'Pri 4', color: '#3b82f6' }  // Blue
  ];

  // Stage options with colors
  const stageOptions = [
    { value: 'Identify', label: 'Identify', color: '#3b82f6' }, // Blue
    { value: 'Document', label: 'Document', color: '#8b5cf6' }, // Purple
    { value: 'Analyze', label: 'Analyze', color: '#06b6d4' }, // Cyan
    { value: 'Testing', label: 'Testing', color: '#f59e0b' }, // Orange
    { value: 'Action', label: 'Action', color: '#ef4444' }, // Red
    { value: 'Institute', label: 'Institute', color: '#22c55e' } // Green
  ];

  useEffect(() => {
    if (editingLesson) {
      setFormData({
        name: editingLesson.name || '',
        serial_number: editingLesson.serial_number || null,
        priority: editingLesson.priority || 'Pri 1',
        references: editingLesson.references || '',
        date: editingLesson.date || new Date().toISOString().split('T')[0],
        lesson_images: editingLesson.lesson_images || [],
        observation_status: editingLesson.observation_status || '',
        dotmplficc: editingLesson.dotmplficc || 'Doctrine',
        ics_organization: editingLesson.ics_organization || 'Command Staff',
        issues_observation: editingLesson.issues_observation || '',
        recommendations: editingLesson.recommendations || '',
        additional_comments: editingLesson.additional_comments || '',
        recommended_actions: editingLesson.recommended_actions || '',
        final_authority_remarks: editingLesson.final_authority_remarks || '',
        testing_done_by: editingLesson.testing_done_by || '',
        procedures_written_by: editingLesson.procedures_written_by || '',
        implement_new_ll_by: editingLesson.implement_new_ll_by || '',
        stage: editingLesson.stage || ''
      });
    }
  }, [editingLesson]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          lesson_images: [...prev.lesson_images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const { dropProps: lessonDropProps, isDragging: isLessonDragging } = useImageDrop({
    onFile: (file) => handleImageUpload({ target: { files: [file] } }),
    multiple: true
  });

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      video.addEventListener('loadedmetadata', () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/png');
        setFormData(prev => ({
          ...prev,
          lesson_images: [...prev.lesson_images, imageData]
        }));
        
        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      lesson_images: prev.lesson_images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Lesson name is required';
    }

    if (!formData.references.trim()) {
      newErrors.references = 'References are required';
    }

    if (!formData.observation_status) {
      newErrors.observation_status = 'Status is required';
    }

    if (!formData.issues_observation.trim()) {
      newErrors.issues_observation = 'Issues/Observation is required';
    }

    if (!formData.recommendations.trim()) {
      newErrors.recommendations = 'Recommendations are required';
    }

    if (!formData.stage) {
      newErrors.stage = 'Stage selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();

    // Guard: without an exercise context we can't save (backend requires exercise_id).
    if (!exerciseId) {
      alert('Cannot save: no exercise context detected. Please open a Lesson Learned from within an exercise.');
      return;
    }

    if (!validateForm()) {
      // Scroll to the first field with an error so the user sees the red highlight
      try {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField && firstErrorField.scrollIntoView) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (_) { /* noop */ }
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        exercise_id: exerciseId
      };

      const url = editingLesson 
        ? `${API_BASE_URL}/api/lessons-learned/${editingLesson.id}`
        : `${API_BASE_URL}/api/lessons-learned`;
      
      const method = editingLesson ? 'PUT' : 'POST';

      const token = secureStorage.getToken?.() || null;
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const savedLesson = await response.json();
        onSave(savedLesson);
      } else {
        let detail = '';
        try {
          const errBody = await response.json();
          if (typeof errBody?.detail === 'string') detail = errBody.detail;
          else if (Array.isArray(errBody?.detail)) detail = errBody.detail.map(d => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(', ');
          else detail = JSON.stringify(errBody).slice(0, 300);
        } catch (_) { /* ignore */ }
        throw new Error(`Save failed (HTTP ${response.status})${detail ? ': ' + detail : ''}`);
      }
    } catch (error) {
      console.error('Error saving lesson learned:', error);
      alert(error.message || 'Failed to save lesson learned. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {!exerciseId && (
        <div className="rounded-md border-2 border-red-500 bg-red-500/10 text-red-300 p-4 text-sm">
          ⚠ No exercise context detected. Open the Lesson Learned view from within an exercise or via the ICS sidebar so the entry can be linked to an Exercise. Saving is disabled until then.
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">
          {editingLesson ? 'Edit Lessons Learned' : 'Create Lessons Learned'}
        </h1>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Lessons Learned
          </Button>
          <Button
            type="submit"
            form="lessons-learned-form"
            disabled={isSubmitting}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-6"
            data-testid="lesson-form-submit-btn-top"
          >
            {isSubmitting ? 'Saving...' : (editingLesson ? 'Update Lesson Learned' : 'Save Lesson Learned')}
          </Button>
        </div>
      </div>

      <form id="lessons-learned-form" onSubmit={handleSubmitLocal} className="space-y-6">
        {/* First Container - Lesson Learned */}
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Lesson Learned</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Name of the lesson learned"
                  data-error={errors.name ? 'true' : undefined}
                  className={`bg-gray-800 text-white placeholder-gray-400 ${errors.name ? 'border-2 border-red-500 ring-1 ring-red-500/40' : 'border border-gray-700'}`}
                />
                {errors.name && (
                  <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label className="text-white">Serial Number</Label>
                <Input
                  value={formData.serial_number || ''}
                  placeholder={editingLesson ? '' : 'Assigned automatically on save'}
                  disabled
                  readOnly
                  className="bg-gray-700 border-gray-600 text-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-numbered per exercise</p>
              </div>

              <div>
                <Label className="text-white">Priority *</Label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white"
                  style={{
                    backgroundColor: priorityOptions.find(p => p.value === formData.priority)?.color + '20',
                    borderColor: priorityOptions.find(p => p.value === formData.priority)?.color
                  }}
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-white">Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">References *</Label>
              <Textarea
                value={formData.references}
                onChange={(e) => handleInputChange('references', e.target.value)}
                placeholder="Enter references..."
                rows={3}
                data-error={errors.references ? 'true' : undefined}
                className={`bg-gray-800 text-white placeholder-gray-400 ${errors.references ? 'border-2 border-red-500 ring-1 ring-red-500/40' : 'border border-gray-700'}`}
              />
              {errors.references && (
                <p className="text-red-400 text-sm mt-1">{errors.references}</p>
              )}
            </div>

            {/* Image Upload */}
            <div
              {...lessonDropProps}
              onClick={() => document.getElementById('lesson-image-upload').click()}
              data-testid="lesson-image-dropzone"
              title="Click to browse or drop images here"
              className={`rounded-lg border-2 border-dashed p-4 cursor-pointer transition-colors ${
                isLessonDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-700 hover:border-orange-500/50'
              }`}
            >
              <Label className="text-white">Supporting Images <span className="text-xs text-gray-400 ml-2">— drag &amp; drop images anywhere in this box</span></Label>
              <div className="flex items-center space-x-4 mt-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="lesson-image-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('lesson-image-upload').click()}
                  className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Image
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCameraCapture}
                  className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Use Camera
                </Button>
              </div>

              {formData.lesson_images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  {formData.lesson_images.map((image, index) => (
                    <div key={`lesson-${index}`} className="relative">
                      <img
                        src={image}
                        alt={`Lesson ${index + 1}`}
                        className="w-full h-24 object-cover rounded border border-gray-700 aspect-square"
                        style={{ aspectRatio: '1/1' }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 h-6 w-6 p-0 border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Second Container - Observations and Recommended Actions */}
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Observations and Recommended Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white">ICS Organization *</Label>
                <select
                  value={formData.ics_organization}
                  onChange={(e) => handleInputChange('ics_organization', e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="Command Staff">Command Staff</option>
                  <option value="Operations">Operations</option>
                  <option value="Planning">Planning</option>
                  <option value="Logistics">Logistics</option>
                  <option value="Fin / Admin">Fin / Admin</option>
                </select>
              </div>

              <div>
                <Label className="text-white">Category *</Label>
                <select
                  value={formData.dotmplficc}
                  onChange={(e) => handleInputChange('dotmplficc', e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 border border-gray-700 text-white"
                  style={{
                    backgroundColor: dotmplficcOptions.find(opt => opt.value === formData.dotmplficc)?.color + '20',
                    borderColor: dotmplficcOptions.find(opt => opt.value === formData.dotmplficc)?.color
                  }}
                >
                  {dotmplficcOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-white">Status *</Label>
                <select
                  value={formData.observation_status || ''}
                  onChange={(e) => handleInputChange('observation_status', e.target.value)}
                  required
                  data-error={errors.observation_status ? 'true' : undefined}
                  className={`w-full p-2 rounded bg-gray-800 text-white ${errors.observation_status ? 'border-2 border-red-500 ring-1 ring-red-500/40' : 'border border-gray-700'}`}
                  style={{
                    backgroundColor: formData.observation_status === 'worked_well' ? '#16a34a20' :
                                     formData.observation_status === 'needs_improvement' ? '#dc262620' : undefined,
                    borderColor: errors.observation_status ? '#ef4444' :
                                 formData.observation_status === 'worked_well' ? '#16a34a' :
                                 formData.observation_status === 'needs_improvement' ? '#dc2626' : undefined
                  }}
                >
                  <option value="">Select Status</option>
                  <option value="worked_well">Worked Well</option>
                  <option value="needs_improvement">Needs Improvement</option>
                </select>
                {errors.observation_status && (
                  <span className="text-red-500 text-xs mt-1 block">{errors.observation_status}</span>
                )}
              </div>
            </div>

            <div>
              <Label className="text-white">Issues / Observation *</Label>
              <Textarea
                value={formData.issues_observation}
                onChange={(e) => handleInputChange('issues_observation', e.target.value)}
                placeholder="Describe the issues or observations..."
                rows={4}
                data-error={errors.issues_observation ? 'true' : undefined}
                className={`bg-gray-800 text-white placeholder-gray-400 ${errors.issues_observation ? 'border-2 border-red-500 ring-1 ring-red-500/40' : 'border border-gray-700'}`}
              />
              {errors.issues_observation && (
                <p className="text-red-400 text-sm mt-1">{errors.issues_observation}</p>
              )}
            </div>

            <div>
              <Label className="text-white">Recommendations *</Label>
              <Textarea
                value={formData.recommendations}
                onChange={(e) => handleInputChange('recommendations', e.target.value)}
                placeholder="Enter your recommendations..."
                rows={4}
                data-error={errors.recommendations ? 'true' : undefined}
                className={`bg-gray-800 text-white placeholder-gray-400 ${errors.recommendations ? 'border-2 border-red-500 ring-1 ring-red-500/40' : 'border border-gray-700'}`}
              />
              {errors.recommendations && (
                <p className="text-red-400 text-sm mt-1">{errors.recommendations}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Third Container - Analysis and Actions */}
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Analysis and Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white">Additional Comments</Label>
              <Textarea
                value={formData.additional_comments}
                onChange={(e) => handleInputChange('additional_comments', e.target.value)}
                placeholder="Enter additional comments..."
                rows={3}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>

            <div>
              <Label className="text-white">Recommended Actions</Label>
              <Textarea
                value={formData.recommended_actions}
                onChange={(e) => handleInputChange('recommended_actions', e.target.value)}
                placeholder="Enter recommended actions..."
                rows={3}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>

            <div>
              <Label className="text-white">Final Authority Remarks</Label>
              <Textarea
                value={formData.final_authority_remarks}
                onChange={(e) => handleInputChange('final_authority_remarks', e.target.value)}
                placeholder="Enter final authority remarks..."
                rows={3}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white">Testing done by</Label>
                <Input
                  type="date"
                  value={formData.testing_done_by}
                  onChange={(e) => handleInputChange('testing_done_by', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Procedures Written By</Label>
                <Input
                  type="date"
                  value={formData.procedures_written_by}
                  onChange={(e) => handleInputChange('procedures_written_by', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Implement new LL by</Label>
                <Input
                  type="date"
                  value={formData.implement_new_ll_by}
                  onChange={(e) => handleInputChange('implement_new_ll_by', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stage Selection Card */}
        <Card
          data-error={errors.stage ? 'true' : undefined}
          className={`bg-gray-900 ${errors.stage ? 'border-2 border-red-500 ring-1 ring-red-500/40' : 'border border-gray-700'}`}
        >
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-orange-500" />
              Lesson Learned Stage
              <span className="text-red-500 ml-1">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-gray-400 text-sm mb-4">
                Select the current stage of this lesson learned (required)
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stageOptions.map((stage) => (
                  <label
                    key={stage.value}
                    className={`
                      relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${formData.stage === stage.value
                        ? 'border-orange-500 bg-gray-800'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                      }
                    `}
                    style={{
                      borderColor: formData.stage === stage.value ? '#f97316' : undefined
                    }}
                  >
                    <input
                      type="radio"
                      name="stage"
                      value={stage.value}
                      checked={formData.stage === stage.value}
                      onChange={(e) => handleInputChange('stage', e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-center w-full">
                      <div
                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center mr-3 flex-shrink-0"
                        style={{
                          borderColor: stage.color,
                          backgroundColor: formData.stage === stage.value ? stage.color : 'transparent'
                        }}
                      >
                        {formData.stage === stage.value && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div
                          className="font-semibold text-sm"
                          style={{ color: stage.color }}
                        >
                          {stage.label}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.stage && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {errors.stage}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Spacer for sticky footer */}
        <div className="h-20"></div>
      </form>

      {/* Sticky Submit Buttons */}
      <div className="sticky bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 p-4 flex justify-end space-x-4 z-10">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="border-gray-600 text-gray-400 hover:bg-gray-700"
        >
          Back to Lessons Learned
        </Button>
        <Button
          type="submit"
          form="lessons-learned-form"
          disabled={isSubmitting}
          className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-8 py-2 text-base"
          data-testid="lesson-form-submit-btn"
        >
          {isSubmitting ? 'Saving...' : (editingLesson ? 'Update Lesson Learned' : 'Save Lesson Learned')}
        </Button>
      </div>
    </div>
  );
};

export default LessonsLearnedForm;
